import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { useTranslation } from 'react-i18next';
import { EndDate, Header, OrganizationName, TableData } from './TableContent.styles';
import { IColumnConfig, ISurveyItem } from './TableContent.types';
import { formatSurveyTypeLabel } from '../../../constans/forms';
import { useStatus, useSurveyTable, useSort } from '../../../hooks';
import { useAuth } from '../../../hooks/useAuth';
import { useDate } from '../../../hooks/useDate';
import StatusFilter from '../../StatusFilter/StatusFilter';

const columnConfig = ({ appliedStatus, appliedSort }: IColumnConfig): ColumnsType<ISurveyItem> => {
  const { t } = useTranslation();
  const { getStatusTag, getStatusKey } = useStatus();
  const { applyFilters } = useSurveyTable();
  const { divideAppliedSort, getFieldOrder } = useSort();
  const { setDateFormat } = useDate();
  const { isEBHRMember } = useAuth();
  const dividedSort = useMemo(() => {
    return divideAppliedSort(appliedSort);
  }, [appliedSort, divideAppliedSort]);
  const onFilterChange = (filters: string[]) => {
    applyFilters({
      status: filters.length !== 0 ? filters.map(filter => getStatusKey(filter)).join() : null,
      pageNo: null,
    });
  };
  return [
    ...(isEBHRMember
      ? [
        {
          title: (
            <Header data-testid="organization-header">
              {t('surveys.table.columnConfig.organization')}
            </Header>
          ),
          dataIndex: 'organization',
          key: 'organization',
          width: '30%',
          render: (_: unknown, { organization }: ISurveyItem) => (
            <OrganizationName
              className="organization-name"
              data-testid="survey-organization-name"
            >
              {organization}
            </OrganizationName>
          ),
        },
      ]
      : []),
    {
      title: <Header data-testid="type">{t('surveys.table.columnConfig.type')}</Header>,
      dataIndex: 'type',
      key: 'type',
      width: '10%',
      render: (_, { type }) => formatSurveyTypeLabel(type),
    },
    {
      title: () => <StatusFilter appliedStatus={appliedStatus} onFilterChange={onFilterChange} />,
      dataIndex: 'status',
      key: 'status',
      width: '9%',
      render: (_, { status }) => getStatusTag(status),
    },
    {
      title: (
        <Header data-testid="survey-name-header">{t('surveys.table.columnConfig.name')}</Header>
      ),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      showSorterTooltip: false,
      defaultSortOrder: (dividedSort ? getFieldOrder('name', dividedSort) : null) as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'],
      width: '30%',
      render: (_, { name }) => (
        <TableData className="survey-name" data-testid="survey-name">
          {name}
        </TableData>
      ),
    },
    {
      title: (
        <Header data-testid="end-date-header">{t('surveys.table.columnConfig.endDate')}</Header>
      ),
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: true,
      showSorterTooltip: false,
      defaultSortOrder: (dividedSort ? getFieldOrder('endDate', dividedSort) : null) as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'],
      width: '21%',
      render: (_, { endDate }) => (
        <EndDate data-testid="survey-end-date">{endDate ? setDateFormat(endDate) : ''}</EndDate>
      ),
    },
  ];
};

export default columnConfig;
